import type { TFunction } from 'i18next';

import {
  collectChips,
  dateRangeChip,
  getMetricOptions,
  optionChips,
  subsetChip,
  type SelectionOption
} from '@/features/dashboard-filters';
import type { ActiveFilterChip } from '@/features/header-filters';

import type { ServiceUsersUrlState } from './types';

interface BuildServiceUsersFilterChipsArgs {
  state: ServiceUsersUrlState;
  defaults: ServiceUsersUrlState;
  t: TFunction;
  locations: SelectionOption[];
  selectedLocations: string[];
  organizations: SelectionOption[];
  selectedOrganizations: string[];
  onRemove: (key: string) => void;
  onResetLocations: () => void;
  onResetOrganizations: () => void;
}

/**
 * Removable chips for the service-users dashboard, in the order the panel lists
 * the filters. Pure, so the accounting can be tested without the hook or the API.
 */
export function buildServiceUsersFilterChips({
  state,
  defaults,
  t,
  locations,
  selectedLocations,
  organizations,
  selectedOrganizations,
  onRemove,
  onResetLocations,
  onResetOrganizations
}: BuildServiceUsersFilterChipsArgs): ActiveFilterChip[] {
  const chip = optionChips(state, defaults, onRemove);

  return collectChips(
    subsetChip({
      key: 'locations',
      title: t('dashboard.locations.label', 'Locations'),
      selected: selectedLocations,
      available: locations,
      onRemove: onResetLocations
    }),
    subsetChip({
      key: 'organizations',
      title: t('dashboard.organizations.label', 'Organizations'),
      selected: selectedOrganizations,
      available: organizations,
      onRemove: onResetOrganizations
    }),
    chip('metric', getMetricOptions(t)),
    dateRangeChip({
      period: state.period,
      fromDate: state.fromDate,
      toDate: state.toDate,
      defaultPeriod: defaults.period,
      t,
      onRemove
    })
  );
}
